import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../lib/i18n";
import { IMAGES, PRODUCT_ORDER } from "../lib/images";
import { MaskedLines } from "../lib/motion";
import { Btn } from "./ui-vision";

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.45, 0.8]);

  return (
    <section ref={ref} className="relative min-h-[100svh] w-full overflow-hidden" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img src={IMAGES.hero} alt="Architecture contemporaine" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-ink" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-ink/40" />

      <div className="relative min-h-[100svh] max-w-[1440px] mx-auto px-6 md:px-12 pt-24 md:pt-28 pb-16 md:pb-24 flex items-center">
        <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          {/* LEFT — headline */}
          <div className="lg:col-span-8">
            <h1 className="font-display font-medium tracking-tight leading-[0.95] text-[15vw] sm:text-[12vw] md:text-[7.5vw] lg:text-[min(6.5vw,93.6px)]">
              <MaskedLines lines={[t.hero.title1]} delay={0.35} />
              <MaskedLines lines={[t.hero.title2]} delay={0.5} lineClassName="text-accent-yellow" />
            </h1>

            <p className="mt-6 md:mt-8 max-w-md text-base md:text-lg font-light text-white/70 leading-relaxed">
              {t.hero.subtitle}
            </p>

            <div className="mt-8 md:mt-10 flex flex-col sm:flex-row sm:flex-wrap lg:flex-nowrap gap-3 md:gap-4">
              <Btn to="/primes" variant="ghost" arrow={false} testid="hero-cta-primes-mobile" className="lg:hidden">
                {t.hero.cta3Short}
              </Btn>
              <Link
                to="/primes"
                data-testid="hero-cta-primes"
                className="group hidden lg:flex items-center justify-between gap-6 w-[50vw] max-w-[750px] py-4 border-y border-white/12 text-white/80 hover:text-accent-yellow transition-colors duration-300"
              >
                <span className="text-base md:text-lg font-light tracking-wide">{t.hero.cta3}</span>
                <ArrowUpRight
                  size={18}
                  className="shrink-0 opacity-50 transition-all duration-300 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </Link>
              <Btn to="/devis" variant="ghost" arrow={false} testid="hero-cta-devis" className="lg:hidden">
                {t.hero.quoteCta}
              </Btn>
              <Btn to="/rendez-vous" variant="solid" arrow={false} testid="hero-cta-quote" className="lg:hidden">
                {t.hero.cta2}
              </Btn>
            </div>
          </div>

          {/* RIGHT (mobile: below content) — Nos expertises */}
          <div
            className="lg:col-span-4 lg:pl-10"
            data-testid="hero-expertises"
          >
            <p className="text-xs uppercase tracking-[0.32em] text-accent-yellow mb-5">
              {t.hero.expertisesTitle}
            </p>
            <nav className="flex flex-col">
              {PRODUCT_ORDER.map(({ key, slug }, i) => (
                <Link
                  key={slug}
                  to={`/produits/${slug}`}
                  data-testid={`hero-expertise-${i}`}
                  className="group flex items-center justify-between gap-6 py-4 border-t border-white/12 last:border-b last:border-white/12 text-white/80 hover:text-accent-yellow transition-colors duration-300"
                >
                  <span className="text-base md:text-lg font-light tracking-wide">{t.products[key].name}</span>
                  <ArrowUpRight
                    size={18}
                    className="shrink-0 opacity-50 transition-all duration-300 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 right-6 md:right-12 hidden md:flex items-center gap-3 text-white/50">
        <span className="text-xs uppercase tracking-[0.25em]">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.span>
      </div>
    </section>
  );
};
